import type {
  CompetitionLink,
  EventLinkWitOpenDate,
  EventTypeLink
} from '@ps/isp-web-services-types';
import { type Alias, select } from '@ps/web-core-store';
import { useTrackers } from '@ps/web-shared-analytics';
import { Link } from '@ps/web-shared-components';
import React from 'react';
import { useSelector } from 'react-redux';

import { TitleWithHighlight } from '@swf/sports/components/TitleWithHighlight/TitleWithHighlight';

import { useSportsMenuWidget } from '../../../../model';

import styles from './SearchResult.scss';

interface Props {
  alias: Alias;
  link:
    | EventTypeLink
    | CompetitionLink
    // TODO https://jira.pyrsoftware.ca/jira/browse/GSFET-1827
    | (EventLinkWitOpenDate & {
        competitionName?: string;
      });
}

const isEventLink = (link: Props['link'], alias: Props['alias']): link is EventLinkWitOpenDate =>
  alias === 'sportsEvent';

function getEventDateFormatted(dateStr: string, locale: string) {
  const date = new Date(dateStr);
  const dayOfWeek = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
  const day = date.getDate();
  const month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);

  return `${dayOfWeek} ${day} ${month}`;
}

const SearchResult: React.FC<Props> = ({ alias, link }) => {
  const { widgetDispatch, widgetSelect } = useSportsMenuWidget();
  const { linkClicked } = useTrackers();
  const searchQuery = useSelector(widgetSelect.searchQuery);
  const locale = useSelector(select.locale.get);

  const searchResultTextWithHighlight = (
    <TitleWithHighlight title={link.text} partToHighlight={searchQuery} />
  );

  const handleSearchResultClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    const target = event.target as HTMLAnchorElement;
    linkClicked({
      linkText: locale === 'en' ? link.text : undefined,
      destinationUrl: target.href
    });
    widgetDispatch.updateSearchQuery('');
  };

  return (
    <li>
      <Link
        className={styles.searchResult}
        alias={alias}
        values={{ ...link }}
        onClick={handleSearchResultClick}
      >
        {searchResultTextWithHighlight}
        {isEventLink(link, alias) ? (
          <span className={styles.secondaryInfo}>
            {getEventDateFormatted(link.openDate, locale)} - {link.competitionName}
          </span>
        ) : null}
      </Link>
    </li>
  );
};

export default SearchResult;
